import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Send, MessageCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhisperBackPage = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userSessionId] = useState(() => {
    return localStorage.getItem('user_session_id') || '';
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (userSessionId) {
      loadChatHistory();
    }
  }, [userSessionId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatHistory = async () => {
    try {
      const response = await axios.get(`${API}/chat/history/${userSessionId}`);
      const formattedHistory = [];
      response.data.forEach(item => {
        formattedHistory.push({
          type: 'user',
          text: item.user_message,
          timestamp: item.timestamp
        });
        formattedHistory.push({
          type: 'ai',
          text: item.ai_response,
          timestamp: item.timestamp
        });
      });
      setMessages(formattedHistory);
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isLoading) return;

    const userMsg = inputMessage.trim();
    setInputMessage('');
    
    // Add user message to UI
    setMessages(prev => [...prev, { type: 'user', text: userMsg, timestamp: new Date().toISOString() }]);
    
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat/whisper-back`, {
        user_session_id: userSessionId,
        message: userMsg
      });

      if (response.data.status === 'success') {
        // Add AI response to UI
        setMessages(prev => [...prev, { 
          type: 'ai', 
          text: response.data.response,
          timestamp: new Date().toISOString()
        }]);
      } else {
        toast.error('Failed to get response');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      
      // Add error message
      setMessages(prev => [...prev, {
        type: 'ai',
        text: "I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const suggestedPrompts = [
    "I'm struggling to set boundaries...",
    "How do I know if I'm healing?",
    "I feel stuck in old patterns",
    "Help me trust myself again"
  ];

  return (
    <div className="min-h-screen bg-[#2E2536] flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#2E2536]/95 backdrop-blur-sm border-b border-[#C8B5E0]/20">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="icon"
              className="text-[#EDE8F3] hover:bg-[#C8B5E0]/20 rounded-full"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            
            <div className="flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-[#E7C6C2]" />
              <h1 className="text-2xl font-bold text-[#EDE8F3]">Whisper Back</h1>
            </div>
            
            <div className="w-10"></div>
          </div>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Welcome Message */}
          {messages.length === 0 && (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#E7C6C2]/20 mb-6">
                <Sparkles className="w-10 h-10 text-[#E7C6C2]" />
              </div>
              <h2 className="text-2xl font-semibold text-[#EDE8F3] mb-3">
                I'm Here to Listen
              </h2>
              <p className="text-[#C8B5E0] max-w-2xl mx-auto mb-8 leading-relaxed">
                This is your safe space to share what's on your heart. I'm not here to fix you or give you therapy—
                I'm here to help you process, reflect, and find clarity. Whatever you're feeling, it's valid. 
                Let's talk it through together.
              </p>

              {/* Suggested Prompts */}
              <div className="space-y-3 max-w-lg mx-auto">
                <p className="text-sm text-[#C8B5E0] mb-3">Try starting with:</p>
                {suggestedPrompts.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => setInputMessage(prompt)}
                    className="w-full text-left px-4 py-3 bg-[#3E324D] hover:bg-[#4A3B5A] text-[#EDE8F3] rounded-lg transition-colors"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="space-y-6">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-6 py-4 ${
                    message.type === 'user'
                      ? 'bg-[#6C4CA2] text-white'
                      : 'bg-[#3E324D] text-[#EDE8F3]'
                  }`}
                >
                  <p className="leading-relaxed whitespace-pre-wrap">{message.text}</p>
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-[#3E324D] rounded-2xl px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-[#C8B5E0] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-[#C8B5E0] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-[#C8B5E0] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      {/* Input Area */}
      <div className="sticky bottom-0 bg-[#2E2536] border-t border-[#C8B5E0]/20 px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={sendMessage} className="flex gap-3">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Share what's on your heart..."
              disabled={isLoading}
              className="flex-1 bg-[#3E324D] text-[#EDE8F3] placeholder-[#6E6275] rounded-full px-6 py-4 focus:outline-none focus:ring-2 focus:ring-[#C8B5E0] disabled:opacity-50"
            />
            <Button
              type="submit"
              disabled={!inputMessage.trim() || isLoading}
              className="bg-[#E7C6C2] hover:bg-[#F7E5E5] text-[#3E324D] rounded-full px-6 py-4 disabled:opacity-50"
            >
              <Send className="w-5 h-5" />
            </Button>
          </form>

          <p className="text-xs text-[#C8B5E0] text-center mt-3">
            💜 This is reflection support, not therapy. For crisis support, call 988.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WhisperBackPage;
